import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  fetchMySkills,
  saveMySkills,
  type UserSkillEntry,
} from '@/api/teamSkills';
import { notifications } from '@/utils/notifications';
import { TeamSkillsAiWizard } from '@/components/settings/TeamSkillsAiWizard';

type Props = {
  className?: string;
};

export function TeamSkillsSettings({ className }: Props) {
  const { t } = useTranslation('settings');
  const [skills, setSkills] = useState<UserSkillEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiWizardOpen, setAiWizardOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const data = await fetchMySkills();
        if (!cancelled) {
          setSkills(data.skills.length ? data.skills : [{ tag: '', description: '' }]);
        }
      } catch {
        if (!cancelled) setSkills([{ tag: '', description: '' }]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateSkill = (index: number, patch: Partial<UserSkillEntry>) => {
    setSkills((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const addRow = () => setSkills((prev) => [...prev, { tag: '', description: '' }]);

  const removeRow = (index: number) => {
    setSkills((prev) => (prev.length <= 1 ? [{ tag: '', description: '' }] : prev.filter((_, i) => i !== index)));
  };

  const handleSave = useCallback(async () => {
    const cleaned = skills
      .map((s) => ({
        tag: s.tag.trim().toLowerCase(),
        description: s.description?.trim() || undefined,
      }))
      .filter((s) => s.tag);
    setSaving(true);
    try {
      await saveMySkills({ skills: cleaned });
      notifications.success(t('skills.saved'));
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : t('skills.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [skills, t]);

  const handleAiApply = (suggested: UserSkillEntry[]) => {
    setSkills(suggested.length ? suggested : [{ tag: '', description: '' }]);
  };

  if (loading) {
    return (
      <div className={className}>
        <Loader2 className="animate-spin text-muted-foreground" size={20} />
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-xl text-xs leading-relaxed text-muted-foreground">{t('skills.hint')}</p>
        <button
          type="button"
          onClick={() => setAiWizardOpen(true)}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3.5 py-2 text-xs font-bold text-primary hover:bg-primary/10"
        >
          <Sparkles size={14} aria-hidden />
          {t('skills.ai.fillWithAi')}
        </button>
      </div>
      <div className="space-y-3">
        {skills.map((skill, index) => (
          <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,8rem)_1fr_auto] sm:items-start">
            <input
              type="text"
              value={skill.tag}
              placeholder={t('skills.tagPlaceholder')}
              onChange={(e) => updateSkill(index, { tag: e.target.value })}
              className="rounded-xl border-0 bg-muted/50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
            <input
              type="text"
              value={skill.description ?? ''}
              placeholder={t('skills.descriptionPlaceholder')}
              onChange={(e) => updateSkill(index, { description: e.target.value })}
              className="rounded-xl border-0 bg-muted/50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
            <button
              type="button"
              onClick={() => removeRow(index)}
              className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={t('skills.removeSkill')}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-col gap-3 border-t border-border-muted pt-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={addRow}
          className="inline-flex w-fit items-center gap-1.5 text-xs font-bold text-primary hover:underline"
        >
          <Plus size={14} />
          {t('skills.addSkill')}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => void handleSave()}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50 sm:w-auto"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : null}
          {t('skills.save')}
        </button>
      </div>

      <TeamSkillsAiWizard
        isOpen={aiWizardOpen}
        onClose={() => setAiWizardOpen(false)}
        onApply={handleAiApply}
      />
    </div>
  );
}
